import type { RegisterDeviceRequest } from '@todo/shared';

/**
 * Identifies THIS browser installation to the API's user_devices table.
 *
 * installationId is a random UUID minted once per browser and kept in
 * localStorage: the same laptop signing in twice updates one row instead of
 * piling up duplicates, while a different browser (or a cleared profile)
 * legitimately counts as a new device. A mobile app does the same thing with
 * its own installation id - and adds the push token this web build does not
 * have yet.
 */

const STORAGE_KEY = 'cgtodo.installationId';
const APP_VERSION = '1.0.0';

function installationId(): string {
  try {
    const existing = localStorage.getItem(STORAGE_KEY);
    if (existing) return existing;
    const minted = crypto.randomUUID();
    localStorage.setItem(STORAGE_KEY, minted);
    return minted;
  } catch {
    // Storage blocked (private mode etc.) - a per-session id is still valid.
    return crypto.randomUUID();
  }
}

/** The id logout needs to tell the API WHICH device row to release. */
export function currentInstallationId(): string {
  return installationId();
}

/** Best-effort OS name from the user agent - a label, not a fingerprint. */
function osVersion(): string {
  const ua = navigator.userAgent;
  if (ua.includes('Mac OS X')) return 'macOS';
  if (ua.includes('Windows')) return 'Windows';
  if (ua.includes('Android')) return 'Android';
  if (/iPhone|iPad/.test(ua)) return 'iOS';
  if (ua.includes('Linux')) return 'Linux';
  return 'Unknown';
}

export function deviceInfo(): RegisterDeviceRequest {
  return {
    platform: 'WEB',
    installationId: installationId(),
    appVersion: APP_VERSION,
    osVersion: osVersion(),
    // pushToken + pushProvider join here once Firebase web push is wired.
  };
}
